import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Mail,
  Send,
  Users,
  FileText,
  AlertCircle,
  Eye,
  Check,
  Loader2,
  Target,
  MessageSquare,
  Gift,
  Heart,
  Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { EmailCampaignService } from '../../services/emailCampaignService';
import { Switch } from '../ui/switch';

interface CreateEmailCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCampaignCreated: () => void;
}

interface CampaignTemplate {
  id: string;
  name: string;
  type: 'newsletter' | 'promotional';
  subject: string;
  content: string;
  icon: React.ReactNode;
  tags: string[];
}

const campaignTemplates: CampaignTemplate[] = [
  {
    id: 'monthly-newsletter',
    name: 'Monthly Newsletter',
    type: 'newsletter',
    subject: 'Monthly Newsletter - What\'s New at JamPack\'d',
    content: `Hi there!

We hope you're having a great month! Here's what's new and exciting at JamPack'd:

🎯 New Products Added
Check out our latest arrivals that we've carefully selected just for you.

📦 Featured Collections
Don't miss our curated collections of must-have items.

🎉 Special Offers
Exclusive deals for our newsletter subscribers.

Thank you for being part of the JamPack'd community!

Best regards,`,
    icon: <MessageSquare className="w-5 h-5" />,
    tags: ['monthly', 'updates', 'community']
  },
  {
    id: 'promotional-campaign',
    name: 'Promotional Campaign',
    type: 'promotional',
    subject: '🎉 Special Offer - Limited Time Only!',
    content: `Hey there!

We have an exciting offer just for you!

💥 SPECIAL HIGHLIGHTS:
• Exclusive discounts on selected items
• Free shipping on qualifying orders
• Limited time only - don't miss out!

🛍️ Shop now and save big on your favourite products. This offer won't last long!

Use your exclusive code at checkout for additional savings!

Happy shopping!`,
    icon: <Gift className="w-5 h-5" />,
    tags: ['promotion', 'discount', 'exclusive']
  },
  {
    id: 'custom',
    name: 'Custom Campaign',
    type: 'newsletter', // Default type, but user can change this
    subject: '',
    content: '',
    icon: <FileText className="w-5 h-5" />,
    tags: ['custom', 'blank']
  }
];

export function CreateEmailCampaignModal({
  isOpen,
  onClose,
  onCampaignCreated,
}: CreateEmailCampaignModalProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<CampaignTemplate | null>(null);
  const [campaignName, setCampaignName] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [campaignType, setCampaignType] = useState<'newsletter' | 'promotional'>('newsletter');
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [recipientsCount, setRecipientsCount] = useState<number>(0);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [sendPlainText, setSendPlainText] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedTemplate(null);
      setCampaignName('');
      setSubject('');
      setContent('');
      setCampaignType('newsletter');
      setSending(false);
      setShowPreview(false);
      setRecipientsCount(0);
      setSendPlainText(false);
    }
  }, [isOpen]);

  // Load recipients count when campaign type changes or modal opens
  useEffect(() => {
    const loadRecipientsCount = async () => {
      if (!campaignType || !isOpen) return;
      
      setLoadingRecipients(true);
      try {
        const result = await EmailCampaignService.getEmailCampaignRecipientsCount(campaignType);
        if (result.success) {
          setRecipientsCount(result.count);
        }
      } catch (error) {
        console.error('Error loading recipients count:', error);
      } finally {
        setLoadingRecipients(false);
      }
    };

    loadRecipientsCount();
  }, [campaignType, isOpen]);

  const handleTemplateSelect = (template: CampaignTemplate) => {
    setSelectedTemplate(template);
    setCampaignName(template.name !== 'Custom Campaign' ? template.name : '');
    setSubject(template.subject);
    setContent(template.content);
    
    // Auto-set campaign type for non-custom templates
    if (template.id !== 'custom') {
      setCampaignType(template.type);
    }
    // For custom templates, keep the current campaign type selection
  };

  const handleCampaignTypeChange = (type: 'newsletter' | 'promotional') => {
    // Always allow change - button disabled state handles the restriction
    setCampaignType(type);
  };

  const handleSendCampaign = async () => {
    if (!campaignName.trim() || !subject.trim() || !content.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSending(true);
    try {
      // Show progress message for immediate sending
      if (recipientsCount > 50) {
        toast.info(`Sending campaign to ${recipientsCount} recipients. This may take a few minutes due to rate limiting...`, {
          duration: 5000
        });
      }
      
      const result = await EmailCampaignService.sendEmailCampaign({
        name: campaignName,
        subject,
        content,
        type: campaignType,
        template: selectedTemplate?.id,
        plainTextOnly: sendPlainText
      });
      
      if (result.success) {
        toast.success('Campaign sent successfully!');
        onCampaignCreated();
        onClose();
      } else {
        toast.error(result.message || 'Failed to send campaign');
      }
    } catch (error) {
      console.error('Error sending campaign:', error);
      toast.error('Failed to send campaign');
    } finally {
      setSending(false);
    }
  };

  const getCampaignTypeInfo = (type: 'newsletter' | 'promotional') => {
    switch (type) {
      case 'newsletter':
        return {
          label: 'Newsletter Subscribers',
          description: 'Sent to all newsletter subscribers',
          color: 'bg-blue-100 text-blue-800',
          icon: <MessageSquare className="w-4 h-4" />
        };
      case 'promotional':
        return {
          label: 'Promotion Subscribers',
          description: 'Sent to customers with promotions enabled',
          color: 'bg-purple-100 text-purple-800',
          icon: <Gift className="w-4 h-4" />
        };
    }
  };

  if (!isOpen) return null;

  const filteredTemplates = campaignTemplates;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ 
            type: "spring",
            duration: 0.5,
            bounce: 0.3 
          }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Enhanced Header */}
          <div className="relative bg-gradient-to-r from-jamaica-green to-green-500 p-6 flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/20 to-transparent"></div>
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-4">
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                  className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                >
                  <Mail className="h-6 w-6 text-white" />
                </motion.div>
                <div>
                  <motion.h2 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-2xl font-bold text-white"
                  >
                    Create Email Campaign
                  </motion.h2>
                  <motion.div 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="flex items-center gap-3 text-white/80 text-sm mt-1"
                  >
                    <div className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {loadingRecipients ? 'Loading...' : `${recipientsCount} recipients`}
                      {!loadingRecipients && recipientsCount > 50 && (
                        <span className="text-xs text-white/60">
                          (~{Math.ceil(recipientsCount / 1.5 / 60)} min send time)
                        </span>
                      )}
                    </div>
                    {campaignType && (
                      <Badge className={getCampaignTypeInfo(campaignType).color}>
                        {getCampaignTypeInfo(campaignType).label}
                      </Badge>
                    )}
                  </motion.div>
                </div>
              </div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={onClose}
                  className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                >
                  <X className="h-5 w-5" />
                </Button>
              </motion.div>
            </div>
            
            {/* Subscriber Group Selection */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-4"
            >
              {/* Helper text above select */}
              {selectedTemplate && selectedTemplate.id !== 'custom' && (
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-white/60 text-xs mb-3"
                >
                  Recipient group is automatically set based on selected template
                </motion.p>
              )}
              {!selectedTemplate && (
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-white/60 text-xs mb-3"
                >
                  Select a template to begin, or choose recipient group for custom campaign
                </motion.p>
              )}
              {selectedTemplate && selectedTemplate.id === 'custom' && (
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-white/60 text-xs mb-3"
                >
                  Choose which subscriber group to send your custom campaign to
                </motion.p>
              )}
              
              <div className="flex items-center gap-3">
                <Label className="text-white/80 text-sm font-medium">
                  Recipients:
                </Label>
                <div className="w-64">
                  <Select 
                    value={campaignType} 
                    onValueChange={(value) => handleCampaignTypeChange(value as 'newsletter' | 'promotional')}
                    disabled={selectedTemplate && selectedTemplate.id !== 'custom'}
                  >
                    <SelectTrigger className={`h-10 bg-white/10 border-white/20 text-white focus:border-white/40 focus:ring-white/20 ${
                      selectedTemplate && selectedTemplate.id !== 'custom' 
                        ? 'cursor-not-allowed opacity-60' 
                        : 'cursor-pointer hover:bg-white/15'
                    }`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newsletter">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" />
                          Newsletter Subscribers
                        </div>
                      </SelectItem>
                      <SelectItem value="promotional">
                        <div className="flex items-center gap-2">
                          <Gift className="w-4 h-4" />
                          Promotional Subscribers
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {selectedTemplate && selectedTemplate.id !== 'custom' && (
                  <span className="text-xs bg-white/20 px-2 py-1 rounded text-white/80 font-medium">
                    Auto
                  </span>
                )}
              </div>
            </motion.div>
          </div>

          {/* Enhanced Content */}
          <div className="flex-1 overflow-hidden flex bg-gradient-to-b from-gray-50/50 to-white">
            {/* Left Side - Template Selection */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="w-1/3 border-r border-gray-200 flex flex-col"
            >
              <div className="p-6 border-b border-gray-200 bg-white">
                <Label className="text-base font-semibold text-gray-900 flex items-center gap-2 mb-4">
                  <Sparkles className="w-4 h-4 text-jamaica-green" />
                  Campaign Templates
                </Label>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {filteredTemplates.map((template, index) => (
                  <motion.div
                    key={template.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Card 
                      className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                        selectedTemplate?.id === template.id 
                          ? 'ring-2 ring-jamaica-green bg-green-50 shadow-lg' 
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => handleTemplateSelect(template)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`${selectedTemplate?.id === template.id ? 'text-jamaica-green' : 'text-gray-400'} mt-0.5`}>
                            {template.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 text-sm leading-tight">{template.name}</h4>
                            <p className="text-xs text-gray-500 mt-1">{template.type}</p>
                            {template.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {template.tags.slice(0, 2).map(tag => (
                                  <span key={tag} className={`text-xs px-2 py-1 rounded-full ${
                                    selectedTemplate?.id === template.id ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          {selectedTemplate?.id === template.id && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="w-5 h-5 bg-jamaica-green rounded-full flex items-center justify-center flex-shrink-0"
                            >
                              <Check className="w-3 h-3 text-white" />
                            </motion.div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right Side - Campaign Composition */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="flex-1 flex flex-col"
            >
              {selectedTemplate ? (
                <>
                  <div className="p-8 space-y-6 flex-1 overflow-y-auto">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-jamaica-green" />
                        Compose Campaign
                      </Label>
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowPreview(!showPreview)}
                          className="flex items-center gap-2 rounded-xl"
                        >
                          <Eye className="w-4 h-4" />
                          {showPreview ? 'Edit' : 'Preview'}
                        </Button>
                      </motion.div>
                    </div>

                    {!showPreview ? (
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="space-y-6"
                      >
                        <div className="space-y-3">
                          <Label htmlFor="campaignName" className="text-sm font-medium text-gray-900">Campaign Name *</Label>
                          <Input
                            id="campaignName"
                            value={campaignName}
                            onChange={(e) => setCampaignName(e.target.value)}
                            placeholder="Enter campaign name..."
                            className="h-12 rounded-xl border-gray-200 focus:border-jamaica-green focus:ring-jamaica-green/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                          />
                        </div>

                        <div className="space-y-3">
                          <Label htmlFor="subject" className="text-sm font-medium text-gray-900">Subject Line *</Label>
                          <Input
                            id="subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="Enter email subject..."
                            className="h-12 rounded-xl border-gray-200 focus:border-jamaica-green focus:ring-jamaica-green/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                          />
                        </div>

                        <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-gray-900">Send as plain text</p>
                            <p className="text-xs text-gray-500">Delivers the campaign without HTML styling or templates.</p>
                          </div>
                          <Switch checked={sendPlainText} onCheckedChange={setSendPlainText} />
                        </div>

                        <div className="space-y-3 flex-1">
                          <Label htmlFor="content" className="text-sm font-medium text-gray-900">Email Content *</Label>
                          <Textarea
                            id="content"
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder="Enter your email content..."
                            className="min-h-[250px] resize-none rounded-xl border-gray-200 focus:border-jamaica-green focus:ring-jamaica-green/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                          />
                        </div>


                      </motion.div>
                    ) : (
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="space-y-4"
                      >
                        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-100 shadow-sm">
                          <CardHeader className="pb-3">
                            <div className="flex items-center gap-2 text-sm text-jamaica-green mb-2">
                              <Mail className="w-4 h-4" />
                              <span className="font-medium">Email Preview</span>
                            </div>
                            <h4 className="font-semibold text-gray-900 text-lg">
                              {subject || 'No subject'}
                            </h4>
                            <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                              <span>To: {getCampaignTypeInfo(campaignType).description}</span>
                              <Badge className={getCampaignTypeInfo(campaignType).color}>
                                {recipientsCount} recipients
                              </Badge>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <div className="bg-white rounded-lg p-4 border border-green-100 shadow-sm">
                              <div className="whitespace-pre-wrap text-gray-700 text-sm leading-relaxed">
                                {content || 'No content'}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    )}
                  </div>

                  {/* Enhanced Footer */}
                  <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
                    <div className="flex items-center justify-end gap-4">
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button 
                          variant="outline" 
                          onClick={onClose}
                          disabled={sending}
                          className="px-6 h-11 rounded-xl font-medium"
                        >
                          Cancel
                        </Button>
                      </motion.div>
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button 
                          onClick={handleSendCampaign}
                          disabled={sending || !campaignName.trim() || !subject.trim() || !content.trim()}
                          className="bg-gradient-to-r from-jamaica-green to-green-500 hover:from-green-700 hover:to-green-600 text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-jamaica-green/25"
                        >
                          {sending ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send Campaign
                            </>
                          )}
                        </Button>
                      </motion.div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center p-8">
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 }}
                    className="text-center"
                  >
                    <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Template</h3>
                    <p className="text-gray-500">Choose a campaign template from the left to get started</p>
                  </motion.div>
                </div>
              )}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
} 
